import { BAND_MINUTES, MAX_MINUTES, RAMP, bandColors, bandLabels, bandMinutesFor } from '../lib/palette';
import type { DisplayMode } from '../lib/types';

interface Props {
  display: DisplayMode;
  /** Heading that says what the colours mean in the current view. */
  caption: string;
}

/** Ticks every 10 minutes, whatever the band width, so the scale reads the same. */
const TICKS = Array.from({ length: MAX_MINUTES / BAND_MINUTES + 1 }, (_, i) => i * BAND_MINUTES);

export function Legend({ display, caption }: Props) {
  const bandMinutes = bandMinutesFor(display);
  const colors = bandColors(bandMinutes);
  const labels = bandLabels(bandMinutes);

  return (
    <div className="legend">
      <div className="legend__caption">{caption}</div>

      {display === 'smooth' ? (
        <div className="legend__continuous">
          <div
            className="legend__gradient"
            style={{ background: `linear-gradient(90deg, ${RAMP.join(', ')})` }}
          />
          <div className="legend__ticks">
            {TICKS.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        </div>
      ) : colors.length <= RAMP.length ? (
        <ol className="legend__bands">
          {colors.map((hex, i) => (
            <li key={hex} className="legend__band">
              <span className="legend__swatch" style={{ background: hex }} />
              <span className="legend__label">{labels[i]}</span>
            </li>
          ))}
        </ol>
      ) : (
        /*
         * Too many bands to label each one — 14 swatches with "35–40" under
         * every one is wider than the panel. Show the steps as one strip and
         * fall back to the same 10-minute ticks the smooth scale uses.
         */
        <div className="legend__continuous">
          <ol className="legend__steps" aria-label={`${bandMinutes}-minute bands`}>
            {colors.map((hex, i) => (
              <li key={hex} style={{ background: hex }} title={`${labels[i]} minutes`} />
            ))}
          </ol>
          <div className="legend__ticks">
            {TICKS.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        </div>
      )}

      <div className="legend__unit">minutes</div>
    </div>
  );
}
